/**
 * Markdown ligero para burbujas del asistente (negritas, saltos, tablas GFM).
 * Sin dependencias externas; el contenido se escapa antes de insertar HTML.
 */

export function escapeHtml(text) {
  if (text == null) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function isTableLine(line) {
  const t = (line || '').trim();
  return t.length > 0 && t.startsWith('|') && t.endsWith('|');
}

function parseTableLine(line) {
  return line
    .trim()
    .slice(1, -1)
    .split('|')
    .map((cell) => cell.trim());
}

function isSeparatorRow(cells) {
  return cells.length > 0 && cells.every((c) => /^:?-{3,}:?$/.test(c));
}

function formatInlineBold(escapedText, boldClass) {
  return escapedText.replace(
    /\*\*(.+?)\*\*/g,
    `<strong class="${boldClass}">$1</strong>`,
  );
}

function formatTableCell(cell, boldClass) {
  return formatInlineBold(escapeHtml(cell), boldClass);
}

function tableRowsToHtml(rows, boldClass) {
  if (!rows || rows.length === 0) return '';

  let bodyStart = 1;
  const header = rows[0];
  if (rows.length > 1 && isSeparatorRow(rows[1])) {
    bodyStart = 2;
  }
  const body = rows.slice(bodyStart);
  if (!header.length) return '';

  let html =
    '<div class="wa-asistente-md-table-wrap"><table class="wa-asistente-md-table"><thead><tr>';
  for (const cell of header) {
    html += `<th scope="col">${formatTableCell(cell, boldClass)}</th>`;
  }
  html += '</tr></thead>';
  if (body.length) {
    html += '<tbody>';
    for (const row of body) {
      html += '<tr>';
      for (let c = 0; c < header.length; c += 1) {
        html += `<td>${formatTableCell(row[c] ?? '', boldClass)}</td>`;
      }
      html += '</tr>';
    }
    html += '</tbody>';
  }
  html += '</table></div>';
  return html;
}

function formatParagraphHtml(text, boldClass) {
  const escaped = escapeHtml(text);
  return formatInlineBold(escaped, boldClass).replace(/\n/g, '<br />');
}

const BLOCK_GAP = '<div class="wa-asistente-md-block-gap" aria-hidden="true"></div>';

/**
 * @param {string} text
 * @param {{ boldClass?: string }} [options]
 */
export function formatAssistantMessageHtml(text, options = {}) {
  const boldClass = options.boldClass || 'font-semibold text-emerald-800';
  if (!text || typeof text !== 'string') return '';

  const lines = text.split('\n');
  const parts = [];
  let paragraph = [];
  let i = 0;

  const flushParagraph = () => {
    if (!paragraph.length) return;
    parts.push(formatParagraphHtml(paragraph.join('\n'), boldClass));
    paragraph = [];
  };

  while (i < lines.length) {
    if (isTableLine(lines[i])) {
      flushParagraph();
      const tableLines = [];
      while (i < lines.length && isTableLine(lines[i])) {
        tableLines.push(lines[i]);
        i += 1;
      }
      const rows = tableLines.map(parseTableLine);
      const tableHtml = tableRowsToHtml(rows, boldClass);
      if (tableHtml) parts.push(tableHtml);
      continue;
    }

    if ((lines[i] || '').trim() === '') {
      flushParagraph();
      i += 1;
      continue;
    }

    paragraph.push(lines[i]);
    i += 1;
  }

  flushParagraph();
  return parts.join(BLOCK_GAP);
}
